import { useEffect, useState } from "react";
import { Bell, Mail, Send, Trash2, Loader2, Package } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminRestock() {
  const [summary, setSummary] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProduct, setActiveProduct] = useState(null);
  const [notifying, setNotifying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/restock-subscriptions");
      setSummary(data.summary || []);
      setSubs(data.subscriptions || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const productSubs = activeProduct
    ? subs.filter((s) => s.product_id === activeProduct.product_id)
    : [];

  const notifyAll = async (product) => {
    if (
      !window.confirm(
        `Envoyer un email à tous les abonnés en attente pour « ${product.product_name} » ? (${product.pending} abonné${product.pending > 1 ? "s" : ""})`
      )
    ) {
      return;
    }
    setNotifying(product.product_id);
    try {
      const { data } = await api.post(
        `/admin/restock-subscriptions/${product.product_id}/notify`,
        {}
      );
      if (data.sent === 0 && data.skipped === 0) {
        toast.info(data.message || "Aucun envoi nécessaire.");
      } else {
        toast.success(`${data.sent} email(s) envoyé(s)${data.skipped ? `, ${data.skipped} en erreur` : ""}.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur");
    } finally {
      setNotifying(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette inscription ?")) return;
    try {
      await api.delete(`/admin/restock-subscriptions/${id}`);
      toast.success("Inscription supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="admin-restock-page">
      <div className="flex items-center gap-3 mb-8">
        <Bell className="h-7 w-7 text-copper" strokeWidth={1.2} />
        <div>
          <h1 className="font-serif text-4xl">Alertes Retour en Stock</h1>
          <p className="text-sm text-ash mt-1">
            Liste des clients qui souhaitent être prévenus lorsque vos produits en rupture
            sont à nouveau disponibles.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-copper" />
        </div>
      ) : summary.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-stone">
          <Mail className="h-10 w-10 text-ash mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-graphite">Aucune inscription pour l&apos;instant.</p>
          <p className="text-xs text-ash mt-1">
            Les clients pourront s&apos;inscrire depuis la fiche produit lorsque le stock est à 0.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Liste produits */}
          <div className="bg-white border border-stone rounded-sm min-w-0">
            <div className="p-4 border-b border-stone">
              <h2 className="font-medium text-ink">Produits suivis ({summary.length})</h2>
            </div>
            <div className="divide-y divide-stone">
              {summary.map((s) => (
                <div
                  key={s.product_id}
                  data-testid={`restock-product-${s.product_slug}`}
                  className={`p-4 hover:bg-sand/50 cursor-pointer transition-colors ${
                    activeProduct?.product_id === s.product_id ? "bg-sand" : ""
                  }`}
                  onClick={() => setActiveProduct(s)}
                >
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-copper shrink-0" strokeWidth={1.4} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink truncate">{s.product_name}</p>
                      <p className="text-xs text-ash">
                        {s.total} inscription{s.total > 1 ? "s" : ""}
                        {s.pending > 0 && (
                          <span className="text-copper">
                            {" · "}
                            {s.pending} en attente
                          </span>
                        )}
                      </p>
                    </div>
                    {s.pending > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          notifyAll(s);
                        }}
                        disabled={notifying === s.product_id}
                        data-testid={`restock-notify-${s.product_slug}`}
                        className="flex items-center gap-1.5 bg-ink text-cream px-3 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-copper transition-colors disabled:opacity-50"
                        title="Notifier les abonnés en attente"
                      >
                        {notifying === s.product_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                        Notifier
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Détails inscriptions */}
          <div className="bg-white border border-stone rounded-sm min-w-0">
            <div className="p-4 border-b border-stone">
              <h2 className="font-medium text-ink">
                {activeProduct ? `Abonnés — ${activeProduct.product_name}` : "Sélectionnez un produit"}
              </h2>
            </div>
            {!activeProduct ? (
              <div className="p-8 text-center text-ash text-sm">
                Cliquez sur un produit à gauche pour voir la liste de ses abonnés.
              </div>
            ) : productSubs.length === 0 ? (
              <div className="p-8 text-center text-ash text-sm">Aucun abonné pour ce produit.</div>
            ) : (
              <div className="divide-y divide-stone max-h-[600px] overflow-y-auto">
                {productSubs.map((sub) => (
                  <div
                    key={sub.id}
                    data-testid={`restock-sub-${sub.id}`}
                    className="p-4 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink text-sm truncate">
                        {sub.first_name ? `${sub.first_name} — ` : ""}{sub.email}
                      </p>
                      <p className="text-xs text-ash mt-0.5">
                        Inscrit le{" "}
                        {new Date(sub.created_at).toLocaleString("fr-FR", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                        {sub.notified_at && (
                          <span className="text-green-700">
                            {" · "}
                            ✓ notifié{" "}
                            {new Date(sub.notified_at).toLocaleDateString("fr-FR")}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => remove(sub.id)}
                      data-testid={`restock-delete-${sub.id}`}
                      className="text-ash hover:text-destructive p-1"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
